<template>
  <div
    v-if="blok && products && products.length"
    v-editable="blok"
    :class="marginClasses"
  >
    <div
      v-if="blok.headline || (blok.cta_url && blok.cta_label)"
      class="mb-10 flex w-full justify-between"
    >
      <h3
        v-if="blok.headline"
        class="text-xl font-bold leading-8 md:text-2xl md:leading-10"
      >
        {{ blok.headline }}
      </h3>

      <NuxtLink
        v-if="blok.cta_url && blok.cta_label"
        class="flex cursor-pointer items-end text-base font-normal leading-5 underline md:text-lg md:leading-6"
        :to="toLocalePath(blok.cta_url)"
      >
        {{ blok.cta_label }}
      </NuxtLink>
    </div>

    <NuxtLazyHydrate when-visible>
      <BasicSlider
        v-if="products"
        :items="products"
        key-prop="id"
        @slide-visible="handleVisibility"
      >
        <template #item="{ item: product, index }">
          <Product
            :key="`product-slider-item-${product.id}`"
            class="px-1"
            :product="product"
            :fetching="productsStatus === 'pending'"
            is-carousel-item
            @click:product="onProductClick($event, index)"
          />
        </template>
      </BasicSlider>
    </NuxtLazyHydrate>
  </div>
</template>

<script setup lang="ts">
import { inject, ref, computed, watch } from 'vue'
import type { PropType } from 'vue'
import type { Product as ProductType } from '@scayle/storefront-nuxt'
import { useRpc } from '#storefront/composables'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import { useRoute } from '#app/composables/router'
import type { SbProductSlider } from '~/cms/storyblok/types/storyblok.d.ts'
import { STORY_KEY } from '#layers/base/constants/injectionKeys'
import type { ListedProduct } from '~/composables/tracking/types'
import { shapeProductCarouselPayloads } from '#layers/base/utils/productPayload'

const props = defineProps({
  blok: {
    type: Object as PropType<SbProductSlider>,
    default: () => ({}),
  },
})

const isLoading = ref(true)

const route = useRoute()

const productIds = computed<number[]>(
  () =>
    props.blok.product_ids?.split(',').map((id: string) => parseInt(id)) || [],
)

const { toLocalePath } = useRouteHelpers()
const { marginClasses } = useStoryblokMargins(props.blok)
const {
  trackSelectItem,
  trackViewItemList,
  trackPromotionSelect,
  trackPromotionView,
} = useTrackingEvents()

const { data: products, status: productsStatus } = useRpc(
  'getProductsByIdsParallel',
  `fetch-products-by-ids-${props.blok._uid}`,
  {
    productIds: productIds.value,
    with: PRODUCT_LISTING_WITH,
    pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
  },
  {
    transform: shapeProductCarouselPayloads,
  },
)

const story = inject(STORY_KEY)

watch(
  products,
  (newProducts) => {
    if (newProducts && newProducts.length > 0) {
      isLoading.value = false
    }
  },
  { immediate: true },
)

const trackingCollector = ref<ProductType[]>([])

const isInspiration = computed(() =>
  route.params?.slug?.includes('inspiration'),
)

const getPromotionTrackingParams = (index: number) => ({
  id: props.blok.promotion_id || 'product_slider_promotion_id',
  name: props.blok.promotion_name || 'product_slider_promotion_name',
  promoType: 'producttile',
  position: index + 1,
  creativeNameSb: props.blok.creative_name || '',
})

const onProductClick = (product: ProductType, index: number) => {
  trackSelectItem({
    product,
    index,
    listType: isInspiration.value ? 'inspiration' : 'product_slider',
    listName: story?.value?.name ?? '',
  })

  trackPromotionSelect(getPromotionTrackingParams(index))
}

const handleVisibility = (
  visibleItems: ListedProduct[],
  slideCount: number,
  slideIndex: number,
) => {
  const untrackedProducts = visibleItems?.filter(
    (product) => !trackingCollector.value.includes(product),
  )

  if (untrackedProducts.length === 0) {
    return
  }

  for (const listedProduct of untrackedProducts) {
    trackPromotionView(getPromotionTrackingParams(listedProduct.index))
  }

  trackingCollector.value.push(...untrackedProducts)

  if (isInspiration.value) {
    trackViewItemList({
      type: 'inspiration',
      name: story?.value?.name ?? '',
      items: visibleItems,
      filters: '',
      filterOptions: '',
      sorting: '',
      totalItemQuantity: products?.value?.length ?? 0,
      totalPages: slideCount ?? 1,
      viewItemQuantity: visibleItems.length,
      currentPage: slideIndex + 1,
    })
  }
}
</script>
